import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Params } from '@angular/router';
import { Company, DynamicField } from '@cargos/sprintpay-models';
import { AppFeaturesShipmentOrSearchForm, DynamicFieldPersistent } from '../../../pages/form-app/models/app-form';
import { FeaturesConfig } from '../../models/organization-data.model';
import { BackgroundImageUploadComponent } from '../background-image-upload/background-image-upload.component';
import { DynamicFieldFormComponent } from '../dynamic-field-form/dynamic-field-form.component';
import {
    DynamicFieldForm,
    DynamicFieldType,
    DynamicFieldValidation,
} from '../dynamic-field-form/models/dynamic-field-form';
import { InputCustomizationComponent } from '../input-customization/input-customization.component';
@Component({
    selector: 'app-search-form',
    imports: [
        FormsModule,
        MatButtonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatRadioModule,
        MatSelectModule,
        MatProgressSpinnerModule,
        MatInputModule,
        MatExpansionModule,
        DynamicFieldFormComponent,
        MatSlideToggleModule,
        BackgroundImageUploadComponent,
        InputCustomizationComponent,
    ],
    templateUrl: './search-form.component.html',
    styleUrl: './search-form.component.scss',
})
export class SearchFormComponent implements OnInit, OnChanges {
    @Input() isEdition: boolean = false;
    @Input() formGroup: FormGroup<AppFeaturesShipmentOrSearchForm>;
    @Input() removeShadowControl?: FormControl<boolean | null>;
    @Input() set features(features: FeaturesConfig | undefined) {
        this.orgBackgroundImages = features?.searchForm?.backgroundImages ?? [];
    }

    public orgBackgroundImages: { name: string | null }[] = [];

    get facilitySource(): FormControl<string | null> | undefined {
        return this.form?.controls['facilitySource'];
    }

    get backgroundImages():
        | FormArray<
              FormGroup<{
                  name: FormControl<string | null>;
              }>
          >
        | undefined {
        return this.form?.controls['backgroundImages'];
    }

    get searchDynamicFields(): FormArray<FormGroup<DynamicFieldForm>> {
        return this.form?.controls['dynamicFields'];
    }

    get backgroundType(): FormControl<string | null> | undefined {
        return this.form?.controls['backgroundType'];
    }

    public isUploadingBackgoundImg = false;
    public fields: DynamicFieldPersistent;
    public form: FormGroup<AppFeaturesShipmentOrSearchForm>;

    ngOnInit(): void {
        this.form = this.formGroup;
        if (this.searchDynamicFields.length === 0) {
            this.setPersistentFields();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['formGroup'] && changes['formGroup'].currentValue) {
            this.form = changes['formGroup'].currentValue;
        }
    }

    setPersistentFields(): void {
        this.fields = {
            name: 'awb',
            label: 'AWB',
            placeholder: 'AWB',
            canDisabled: false,
            isRequired: true,
            fieldType: DynamicFieldType.INPUT,
            selectedValidators: ['required'],
        };
        this.addPersistentField();
    }

    addPersistentField(): void {
        if (this.isEdition) {
            return;
        }

        this.addSearchFormDynamicField(<DynamicField>{
            name: this.fields.name,
            label: this.fields.label,
            placeholder: this.fields.placeholder,
            isDisabled: this.fields.name === 'hawbId',
            isRequired: this.fields.isRequired,
            fieldType: this.fields.fieldType,
            options: this.fields.options,
        });
    }

    addSearchFormDynamicField(field?: DynamicField): void {
        const nameValidators: ValidatorFn[] = [Validators.required];
        const typeValidators: ValidatorFn[] = [Validators.required];
        let labelValidators: ValidatorFn[] = [];
        let placeholderValidators: ValidatorFn[] = [];
        labelValidators = [Validators.required];
        placeholderValidators = [Validators.required];

        this.searchDynamicFields.push(
            new FormGroup<DynamicFieldForm>({
                name: new FormControl<string>(field?.name || '', nameValidators),
                label: new FormControl<string>(field?.label || '', labelValidators.length ? labelValidators : null),
                placeholder: new FormControl<string>(
                    field?.placeholder || '',
                    placeholderValidators.length ? placeholderValidators : null
                ),
                isDisabled: new FormControl<boolean>(field?.isDisabled || false),
                fieldType: new FormControl<string>(field?.fieldType || DynamicFieldType.INPUT, typeValidators),
                options: new FormArray<FormControl<string>>(
                    field?.options
                        ? field.options.map(
                              (opt) =>
                                  new FormControl<string>(opt.value as string, {
                                      nonNullable: true,
                                  })
                          )
                        : []
                ),
                selectedValidators: new FormControl<string[] | null>(
                    field?.validations && field.validations.length
                        ? field.validations.flatMap((validation) => validation.name as string)
                        : null
                ),
                validations: new FormArray<FormGroup<DynamicFieldValidation>>(
                    field?.validations
                        ? field.validations.map(
                              (validation) =>
                                  new FormGroup<DynamicFieldValidation>({
                                      id: new FormControl<number | null>(validation.id ?? null),
                                      name: new FormControl<string | null>(validation.name ?? null),
                                      value: new FormControl<string | RegExp | string[] | RegExp[] | null>(
                                          validation.value ?? null
                                      ),
                                      message: new FormControl<string | null>(validation.message ?? null),
                                      title: new FormControl<string | null>(validation.title ?? null),
                                      type: new FormControl<string | null>(validation.type ?? null),
                                      apiValidator: new FormControl<string | null>(validation.apiValidator ?? null),
                                      params: new FormControl<Params[] | null>(validation.params ?? null),
                                      typeRequest: new FormControl<string | null>(validation.typeRequest ?? null),
                                  })
                          )
                        : []
                ),
                id: new FormControl<number | null>(field?.id || null),
                section: new FormControl<number | null>(field?.section || null),
                company: new FormControl<Company | null>(field?.company || null),
                emailDomain: new FormControl<string | null>(field?.emailDomain || null),
                isReplacement: new FormControl<boolean | null>(field?.isReplacement || null),
                isHidden: new FormControl<boolean | null>(field?.isHidden || null),
                subLabel: new FormControl<string | null>(field?.subLabel || null),
                tooltipTitle: new FormControl<string | null>(field?.tooltipTitle || null),
                tooltipMessage: new FormControl<string | null>(field?.tooltipMessage || null),
                hint: new FormControl<string | null>(field?.hint || null),
                value: new FormControl<string | null>(field?.value || null),
                isDynamic: new FormControl<boolean | null>(field?.isDynamic || null),
            })
        );
    }

    deleteDynamicField(index: number): void {
        this.searchDynamicFields.removeAt(index);
    }
}
